import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { createApiClient } from "../../api/apiClient";
import { handleStandUpErrorWithFriendlyMessage } from "../utils/standUpErrorHandler";
import { STANDUP_API_NAMES } from "./standUpSlice";

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

// Lighthouse Execution Types (Review Performance Tab)
export interface LighthouseExecutionLocation {
  locationId: number;
  locationName: string;
  year: number;
  leadershipWalksPercentage: number;
  pulseChecksPercentage: number;
  shiftHandoversPercentage: number;
  sparcWalksPercentage: number;
  observationsPercentage: number;
  LHExecutionPercentage: number;
  period?: number;
  week?: number;
}

export interface LighthouseExecutionRegion {
  regionName: string;
  locationCount: number;
  locations: number[];
  leadershipWalksPercentage: number;
  pulseChecksPercentage: number;
  shiftHandoversPercentage: number;
  sparcWalksPercentage: number;
  observationsPercentage: number;
  LHExecutionPercentage: number;
}

export interface LighthouseExecutionDivision {
  divisionName: string;
  locationCount: number;
  locations: number[];
  leadershipWalksPercentage: number;
  pulseChecksPercentage: number;
  shiftHandoversPercentage: number;
  sparcWalksPercentage: number;
  observationsPercentage: number;
  LHExecutionPercentage: number;
}

export interface LighthouseExecutionTimeframe {
  timeframe: string;
  year: number;
  isYearToDate: boolean;
  location: LighthouseExecutionLocation;
  region: LighthouseExecutionRegion;
  division: LighthouseExecutionDivision;
  period?: number;
  week?: number;
}

export interface LighthouseExecutionData {
  LCW: LighthouseExecutionTimeframe;
  LCP: LighthouseExecutionTimeframe;
  YTD: LighthouseExecutionTimeframe;
  CURRENT: LighthouseExecutionTimeframe;
}

export interface LighthouseModuleTimeframe {
  timeframe: string;
  location: {
    locationId: number;
    locationName: string;
  };
  year: number;
  isYearToDate: boolean;
  value: number;
  kpi_id: string;
}

export interface LighthouseModule {
  LCW: LighthouseModuleTimeframe;
  LCP: LighthouseModuleTimeframe;
  YTD: LighthouseModuleTimeframe;
  CURRENT: LighthouseModuleTimeframe;
}

export interface LighthouseExecutionResponse {
  success: boolean;
  data: {
    location_name: string;
    modules: {
      "Pulse Check": LighthouseModule;
      "Observation": LighthouseModule;
      "Shift Handover": LighthouseModule;
      "Sparc Walk": LighthouseModule;
      "Leadership Walk": LighthouseModule;
    };
  };
  metadata: {
    fetchedAt: string;
    timeframesCount: number;
    hasErrors: boolean;
    successCount: number;
  };
}

// ============================================================================
// STATE INTERFACE
// ============================================================================

// Favorites Types
export interface FavoriteItem {
  favourite_id: number;
  standup_session_id: string;
  location_id: string;
  location_name: string;
  standup_type: string;
  item_type: string;
  item_id: string;
  item_name: string;
  is_favourite: boolean;
  created_by: string;
  created_date?: string;
  modified_by?: string;
  modified_date?: string;
}

// Top Opportunities Types
export interface TopOpportunityItem {
  action_plan_id: string;
  title: string;
  assigned_user: string;
  status: string;
  target_date: string;
  source: string;
  action_plan_owner: string;
  type: string;
  start_date: string;
  frontline_employee: string;
  action_plan: string;
  [key: string]: any; // For additional fields
}

// Lighthouse Tasks Types
export interface LighthouseTask {
  kfa_id: string;
  kfa: string;
  kfa_type: string;
  module_name: string;
  severity?: string;
  total_selections?: number;
  [key: string]: any;
}

export interface LighthouseTaskCategory {
  category: string;
  tasks: Array<{
    id: string;
    kfa_id: string;
    title: string;
    subHeader: string;
    description: string;
    module_name: string;
    kfa_type?: string; // Add kfa_type field to preserve it from API response
  }>;
}

// KPI Metrics Types (Review Performance Tab)
export interface KpiMetric {
  id: string;
  kpiCode?: string;
  kpiId?: string;
  title: string;
  badgeLetter: string;
  badgeColor: string;
  isMandatory?: boolean;
  actual: number | null;
  target: number | null;
  delta: number | null;
  chartData: Array<{ date: string; value: number | null }>;
}

export interface KpiMetricsResponse {
  metrics: Array<{
    kpi_id: string;
    kpi_code?: string;
    kpi: string;
    category: string;
    actual_value: number | null;
    target: number | null;
    cadences_value: Array<{ [date: string]: number | null }>;
  }>;
}

export interface StandupRedirectState {
  // Session Data (stored when user creates/opens a standup)
  session: {
    sessionId: string; // Generated session ID or actual standup ID
    standupType: string; // daily_lt, daily_flt, weekly_lt
    standupDate: string; // Selected date range
    standupTitle: string; // Generated title from type + date
    isEditMode?: boolean; // True when editing existing standup
    isViewMode?: boolean; // True when viewing existing standup
    standupId?: string; // Actual standup ID from database (for edit/view modes)
  };

  // Common State (shared across tabs)
  activeTab: string;
  favoriteOpportunities: string[];
  isTopOpportunitiesExpanded: boolean;
  
  // Favorites State (shared across Review Performance and Key Activities)
  favorites: {
    data: FavoriteItem[];
    loading: boolean;
    error: string | null;
    lastFetchedParams: {
      sessionId: string;
      locationId: string;
      standupType: string;
    } | null;
    taskToFavoriteIdMap: { [taskId: string]: number };
  };

  // Review Performance Tab State
  reviewPerformance: {
    favoriteSparcCards: string[];
    favoriteLighthouseCards: string[];
    selectedPeriod: string;
    lighthouseExecution: {
      data: {
        location_name: string;
        modules: {
          "Pulse Check": LighthouseModule;
          "Observation": LighthouseModule;
          "Shift Handover": LighthouseModule;
          "Sparc Walk": LighthouseModule;
          "Leadership Walk": LighthouseModule;
        };
      } | null;
      loading: boolean;
      error: string | null;
      lastFetchedLocation: string | null;
    };
    kpiMetrics: {
      data: KpiMetric[];
      loading: boolean;
      error: string | null;
      lastFetchedParams: {
        location: string;
        standupType: string;
        metricsType: string;
      } | null;
    };
  };

  // Key Activities Tab State
  keyActivities: {
    favoriteTasks: string[];
    allTasks: any[];
    loading: boolean;
    error: string | null;
  };

  // News Tab State
  news: {
    sessionNews: any[];
    newsTitle: string;
    newsDescription: string;
    loading: boolean;
    error: string | null;
  };

  // Review Progress Tab State
  reviewProgress: {
    currentNewsPage: number;
    selectedKpi: any[];
  };
}

// ============================================================================
// INITIAL STATE
// ============================================================================

const initialState: StandupRedirectState = {
  session: {
    sessionId: '',
    standupType: '',
    standupDate: '',
    standupTitle: '',
  },

  activeTab: 'review-performance',
  favoriteOpportunities: [],
  isTopOpportunitiesExpanded: false,
  
  favorites: {
    data: [],
    loading: false,
    error: null,
    lastFetchedParams: null,
    taskToFavoriteIdMap: {},
  },

  reviewPerformance: {
    favoriteSparcCards: [],
    favoriteLighthouseCards: [],
    selectedPeriod: 'Daily',
    lighthouseExecution: {
      data: null,
      loading: false,
      error: null,
      lastFetchedLocation: null,
    },
    kpiMetrics: {
      data: [],
      loading: false,
      error: null,
      lastFetchedParams: null,
    },
  },

  keyActivities: {
    favoriteTasks: [],
    allTasks: [],
    loading: false,
    error: null,
  },

  news: {
    sessionNews: [],
    newsTitle: '',
    newsDescription: '',
    loading: false,
    error: null,
  },

  reviewProgress: {
    currentNewsPage: 0,
    selectedKpi: [],
  },
};

// ============================================================================
// ASYNC THUNKS
// ============================================================================

/**
 * Fetch lighthouse execution data for Review Performance tab
 */
export const fetchLighthouseExecution = createAsyncThunk(
  "standupRedirect/fetchLighthouseExecution",
  async (locationName: string, { rejectWithValue }) => {
    try {
      const apiClient = createApiClient();
      const response = await apiClient.get(
        `/stand-up/lh-execution?location_name=${encodeURIComponent(locationName)}`
      );

      // Treat 200 and 201 as successful responses — but consider empty/null payloads as "no data"
      if (response.status === 200 || response.status === 201) {
        const payload = response?.data;
        const payloadData = payload?.data ?? payload; // some APIs wrap under .data

        const hasData = payloadData !== null && payloadData !== undefined && (
          (typeof payloadData === 'object' && Object.keys(payloadData).length > 0) ||
          (Array.isArray(payloadData) && payloadData.length > 0)
        );

        if (!hasData) {
          // For 200/201 with no data: surface a simple inline/text message (no overlay/modal)
          const noDataMessage = `No lighthouse execution data found for '${locationName}'.`;
          return rejectWithValue(noDataMessage);
        }

        return {
          data: payload as LighthouseExecutionResponse,
          locationName,
        };
      }

      // Non-200/201 statuses should surface the overlay/modal via centralized handler
      try {
        const msg = `API returned status ${response.status}`;
        handleStandUpErrorWithFriendlyMessage(
          STANDUP_API_NAMES.FETCH_LIGHTHOUSE_EXECUTION,
          msg,
          true,
          { locationName, status: response.status }
        );
      } catch (handlerErr) {
        console.error('Error while invoking standup error handler for lh-execution non-200 response:', handlerErr);
      }

      return rejectWithValue(`Failed to fetch lighthouse execution data (status ${response.status})`);
    } catch (error: any) {
      console.error("Error fetching lighthouse execution data:", error);
      const status = error?.response?.status;
      let errorMessage = "Failed to fetch lighthouse execution data";

      // For HTTP 404 or other response errors, show overlay/modal via centralized handler
      if (status === 404) {
        errorMessage = `No lighthouse execution data found for '${locationName}'.`;
        try {
          handleStandUpErrorWithFriendlyMessage(
            STANDUP_API_NAMES.FETCH_LIGHTHOUSE_EXECUTION,
            error,
            true,
            { locationName }
          );
        } catch (handlerErr) {
          console.error('Error while invoking standup error handler:', handlerErr);
        }

        return rejectWithValue(errorMessage);
      }

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      try {
        handleStandUpErrorWithFriendlyMessage(
          STANDUP_API_NAMES.FETCH_LIGHTHOUSE_EXECUTION,
          error,
          true,
          { locationName }
        );
      } catch (handlerErr) {
        console.error('Error while invoking standup error handler:', handlerErr);
      }

      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Fetch favorites for the standup session (used in both Review Performance and Key Activities tabs)
 * 
 * API: GET /stand-up/favourites
 * Query Parameters:
 * - standup_session_id: Session ID for new standup creation/editing
 * - standup_id: Used when in edit/view mode (actual database standup ID)
 * - standup_session_id: Used for new standups (generated session ID)
 * - location_name: Selected location display text
 * - standup_type: Type of standup (daily_lt, daily_flt, weekly_lt)
 * 
 * This fetches all favorited items (KFAs, KPIs, processes) for the standup session or record
 */
export const fetchFavorites = createAsyncThunk(
  "standupRedirect/fetchFavorites",
  async (
    params: {
      standupSessionId: string;
      standupId?: string; // Actual database standup ID for edit/view modes
      locationId: string;
      locationName: string;
      standupType: string;
      isEditMode?: boolean;
      isViewMode?: boolean;
    },
    { rejectWithValue }
  ) => {
    try {
      const apiClient = createApiClient();
      
      // Build query string based on mode
      let apiUrl = `/stand-up/favourites?`;
      
      if (params.isEditMode && params.standupId) {
        // Edit mode: include both standup_id and standup_session_id
        apiUrl += `standup_id=${encodeURIComponent(params.standupId)}&standup_session_id=${encodeURIComponent(params.standupSessionId)}`;
      } else if (params.isViewMode && params.standupId) {
        // View mode: include standup_id only
        apiUrl += `standup_id=${encodeURIComponent(params.standupId)}&standup_session_id=${encodeURIComponent(params.standupSessionId)}`;
      } else {
        // New mode: include standup_session_id only
        apiUrl += `standup_session_id=${encodeURIComponent(params.standupSessionId)}`;
      }
      
      apiUrl += `&location_id=${params.locationId}&location_name=${encodeURIComponent(
        params.locationName
      )}&standup_type=${encodeURIComponent(params.standupType)}`;
      
      console.log('Fetching favorites from API:', apiUrl);
      const response = await apiClient.get(apiUrl);
      console.log('Favorites API response:', response);

      if (response.status === 200) {
        console.log('Favorites API response.data:', response.data);
        return {
          data: response.data.data as FavoriteItem[],
          params,
        };
      } else {
        throw new Error(`API returned status ${response.status}`);
      }
    } catch (error: any) {
      console.error("Error fetching favorites:", error);
      let errorMessage = "Failed to fetch favorites";

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      try {
        handleStandUpErrorWithFriendlyMessage(
          STANDUP_API_NAMES.FETCH_FAVORITES,
          error,
          true,
          { params }
        );
      } catch (handlerErr) {
        console.error('Error while invoking standup error handler:', handlerErr);
      }

      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Fetch lighthouse tasks data upfront at redirect screen level
 * GET /stand-up/lighthouse_tasks with location and period parameters
 */
export const fetchLighthouseTasks = createAsyncThunk(
  "standupRedirect/fetchLighthouseTasks",
  async (
    params: {
      locationId: string;
      locationName: string;
      period: number;
      year: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const apiClient = createApiClient();
      const apiUrl = `/stand-up/lighthouse_tasks?location_id=${encodeURIComponent(
        params.locationId
      )}&location_name=${encodeURIComponent(
        params.locationName
      )}&period=${encodeURIComponent(params.period)}&year=${encodeURIComponent(
        params.year
      )}`;

      const response = await apiClient.get(apiUrl);

      if (response.status === 200) {
        // Transform raw API data into categorized format
        const apiResponse = response.data;
        const rawData = apiResponse?.data || []; // Extract the data array from {success: true, data: [...]}
        const categorizedData: { [key: string]: any[] } = {};
        
        if (Array.isArray(rawData)) {
          rawData.forEach((item: any) => {
            const moduleName = item.module_name;
            if (!categorizedData[moduleName]) {
              categorizedData[moduleName] = [];
            }
            
            // Format subtitle based on module
            let subtitle = '';
            if (moduleName === 'Racking Inspection') {
              subtitle = item.severity || 'N/A';
            } else {
              subtitle = `Occurrences: ${item.total_selections || 0}`;
            }
            
            categorizedData[moduleName].push({
              id: `${moduleName}-${item.kfa_id}`,
              kfa_id: item.kfa_id,
              title: item.kfa_type || 'Task',
              subHeader: subtitle,
              description: item.kfa || '',
              module_name: moduleName,
              kfa_type: item.kfa_type || '', // Preserve kfa_type from API response
            });
          });
        }
        
        // Convert to array format
        const formattedData: LighthouseTaskCategory[] = Object.entries(categorizedData).map(
          ([category, tasks]) => ({
            category,
            tasks,
          })
        );

        return {
          data: formattedData,
          params,
        };
      } else {
        // Non-200 status: show overlay/modal via centralized handler
        const errorMsg = `API returned status ${response.status}`;
        try {
          handleStandUpErrorWithFriendlyMessage(
            STANDUP_API_NAMES.FETCH_LIGHTHOUSE_TASKS,
            errorMsg,
            true,
            { params, status: response.status }
          );
        } catch (handlerErr) {
          console.error('Error while invoking standup error handler for lighthouse_tasks non-200 response:', handlerErr);
        }
        return rejectWithValue(`Failed to fetch lighthouse tasks (status ${response.status})`);
      }
    } catch (error: any) {
      console.error("Error fetching lighthouse tasks:", error);
      let errorMessage = "Failed to fetch lighthouse tasks";

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      try {
        handleStandUpErrorWithFriendlyMessage(
          STANDUP_API_NAMES.FETCH_LIGHTHOUSE_TASKS,
          error,
          true,
          { params }
        );
      } catch (handlerErr) {
        console.error('Error while invoking standup error handler:', handlerErr);
      }

      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Fetch KPI metrics data for Review Performance tab
 * GET /stand-up/kpi-metrics-v2 with location, standupType, and metricsType parameters
 */
export const fetchKpiMetrics = createAsyncThunk(
  "standupRedirect/fetchKpiMetrics",
  async (
    params: {
      location: string;
      standupType: string;
      metricsType: string;
      standupId?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const apiClient = createApiClient();
      
      // Helper function to map category to badge letter
      const getCategoryBadge = (category: string): string => {
        const categoryMap: { [key: string]: string } = {
          'Safety': 'S',
          'Accuracy': 'A',
          'Reliability': 'R',
          'People': 'P',
          'Cost': 'C',
        };
        return categoryMap[category] || 'S';
      };

      // Helper function to map category to badge color
      const getCategoryColor = (category: string): string => {
        const colorMap: { [key: string]: string } = {
          'Safety': '#D4F4DD',
          'Accuracy': '#FFF4D4',
          'Reliability': '#D4E4FF',
          'People': '#E3F2FD',
          'Cost': '#FDD4D4',
        };
        return colorMap[category] || '#D4F4DD';
      };

      // Helper function to check if KPI is percentage-based and needs value multiplication
      const isPercentageKpi = (kpiName: string): boolean => {
        const percentageKpis = [
          'BPA',
          'Build Location Fill Rate',
          'DPS',
          'HAULBACK',
          'In House Cut',
          'KU',
          'MLP',
          'On Time',
          'Over Time',
          'Self Inflicted Audit',
        ];
        const normalizedKpiName = kpiName.toLowerCase().replace(/[%\s]/g, '');
        return percentageKpis.some(pKpi => {
          const normalizedPKpi = pKpi.toLowerCase().replace(/[%\s]/g, '');
          return normalizedKpiName.includes(normalizedPKpi) || 
                 normalizedPKpi.includes(normalizedKpiName) ||
                 normalizedKpiName === normalizedPKpi;
        });
      };

      // Helper function to format a backend date string (YYYY-MM-DD) for UI display as M/D.
      // Parses the string directly instead of using `new Date()` to avoid local timezone conversion.
      const formatChartDate = (dateString: string): string => {
        const parts = dateString.split('-');
        if (parts.length === 3) {
          const month = parseInt(parts[1], 10);
          const day = parseInt(parts[2], 10);
          return `${month}/${day}`;
        }
        return dateString;
      };

      // Normalizes the Gap Time KPI title regardless of the suffix the backend returns
      const formatKpiTitle = (kpiName: string): string => {
        return kpiName?.trim().toUpperCase().startsWith('GAP TIME')
          ? 'Gap Time (Avg Min / Operator)'
          : kpiName;
      };

      // standupType is already transformed when passed from the screen
      const apiUrl = `/stand-up/kpi-metrics-v2?location=${encodeURIComponent(
        params.location
      )}&standUpType=${params.standupType}&metricsType=${params.metricsType.toLowerCase()}${params.standupId ? `&standup_id=${encodeURIComponent(params.standupId)}` : ''}`;

      console.log('Fetching KPI metrics from API:', apiUrl);
      const response = await apiClient.get(apiUrl);

      if (response.status === 200) {
        console.log('KPI metrics API response:', response.data);
        
        if (response?.data?.metrics || response?.data?.data?.metrics) {
          const metricsData: KpiMetricsResponse = response?.data?.metrics 
            ? response?.data 
            : response?.data?.data;
          const transformedCards: KpiMetric[] = metricsData.metrics.map((metric: any, index: number) => {
            // Check if this KPI is percentage-based
            const isPercentage = isPercentageKpi(metric.kpi);
            const multiplier = isPercentage ? 100 : 1;

            // Transform only the latest 7 cadence points to chartData format
            const latestCadences = Array.isArray(metric.cadences_value)
              ? metric.cadences_value.slice(-7)
              : [];
            const chartData = latestCadences.map((cadence: any) => {
              const dateKey = Object.keys(cadence)[0];
              const value = cadence[dateKey];
              return {
                date: formatChartDate(dateKey),
                value: value == null ? null : value * multiplier,
              };
            });

            const actualValue = metric.actual_value == null ? null : metric.actual_value * multiplier;
            const targetValue = metric.target == null ? null : metric.target * multiplier;

            return {
              id: metric.kpi_code || metric.kpi_id,
              kpiCode: metric.kpi_code || '',
              kpiId: metric.kpi_id,
              title: formatKpiTitle(metric.kpi),
              badgeLetter: getCategoryBadge(metric.category),
              badgeColor: getCategoryColor(metric.category),
              isMandatory: Boolean(metric.isMandatory),
              actual: actualValue,
              target: targetValue,
              delta: actualValue == null || targetValue == null ? null : actualValue - targetValue,
              chartData: chartData,
            };
          });

          return {
            data: transformedCards,
            params,
          };
        } else {
          return {
            data: [],
            params,
          };
        }
      } else {
        throw new Error(`API returned status ${response.status}`);
      }
    } catch (error: any) {
      console.error("Error fetching KPI metrics:", error);
      let errorMessage = "Failed to fetch KPI metrics";

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      try {
        handleStandUpErrorWithFriendlyMessage(
          STANDUP_API_NAMES.FETCH_KPI_METRICS,
          error,
          true,
          { params }
        );
      } catch (handlerErr) {
        console.error('Error while invoking standup error handler:', handlerErr);
      }

      return rejectWithValue(errorMessage);
    }
  }
);

// ============================================================================
// SLICE
// ============================================================================

const standupRedirectSlice = createSlice({
  name: "standupRedirect",
  initialState,
  reducers: {
    // ========================================================================
    // SESSION ACTIONS
    // ========================================================================
    
    setSessionData: (state, action: PayloadAction<Partial<{
      sessionId: string;
      standupType: string;
      standupDate: string;
      standupTitle: string;
      isEditMode?: boolean;
      isViewMode?: boolean;
      standupId?: string;
    }>>) => {
      state.session = { ...state.session, ...action.payload };
    },

    clearSessionData: (state) => {
      state.session = initialState.session;
    },

    // ========================================================================
    // COMMON ACTIONS
    // ========================================================================
    
    setActiveTab: (state, action: PayloadAction<string>) => {
      state.activeTab = action.payload;
    },

    toggleTopOpportunities: (state) => {
      state.isTopOpportunitiesExpanded = !state.isTopOpportunitiesExpanded;
    },

    toggleFavoriteOpportunity: (state, action: PayloadAction<string>) => {
      const opportunityId = action.payload;
      const index = state.favoriteOpportunities.indexOf(opportunityId);
      
      if (index > -1) {
        state.favoriteOpportunities.splice(index, 1);
      } else {
        state.favoriteOpportunities.push(opportunityId);
      }
    },

    // ========================================================================
    // REVIEW PERFORMANCE TAB ACTIONS
    // ========================================================================
    
    clearLighthouseExecution: (state) => {
      state.reviewPerformance.lighthouseExecution.data = null;
      state.reviewPerformance.lighthouseExecution.error = null;
      state.reviewPerformance.lighthouseExecution.lastFetchedLocation = null;
    },

    toggleFavoriteSparcCard: (state, action: PayloadAction<string>) => {
      const cardId = action.payload;
      const index = state.reviewPerformance.favoriteSparcCards.indexOf(cardId);
      
      if (index > -1) {
        state.reviewPerformance.favoriteSparcCards.splice(index, 1);
      } else {
        state.reviewPerformance.favoriteSparcCards.push(cardId);
      }
    },

    toggleFavoriteLighthouseCard: (state, action: PayloadAction<string>) => {
      const cardId = action.payload;
      const index = state.reviewPerformance.favoriteLighthouseCards.indexOf(cardId);
      
      if (index > -1) {
        state.reviewPerformance.favoriteLighthouseCards.splice(index, 1);
      } else {
        state.reviewPerformance.favoriteLighthouseCards.push(cardId);
      }
    },

    setSelectedPeriod: (state, action: PayloadAction<string>) => {
      state.reviewPerformance.selectedPeriod = action.payload;
    },

    // ========================================================================
    // KEY ACTIVITIES TAB ACTIONS
    // ========================================================================
    
    toggleFavoriteLighthouseTask: (state, action: PayloadAction<string>) => {
      const taskId = action.payload;
      const index = state.keyActivities.favoriteTasks.indexOf(taskId);
      
      if (index > -1) {
        state.keyActivities.favoriteTasks.splice(index, 1);
      } else {
        state.keyActivities.favoriteTasks.push(taskId);
      }
    },

    setAllLighthouseTasks: (state, action: PayloadAction<any[]>) => {
      state.keyActivities.allTasks = action.payload;
    },

    // ========================================================================
    // NEWS TAB ACTIONS
    // ========================================================================
    
    setNewsTitle: (state, action: PayloadAction<string>) => {
      state.news.newsTitle = action.payload;
    },

    setNewsDescription: (state, action: PayloadAction<string>) => {
      state.news.newsDescription = action.payload;
    },

    addNewsItem: (state, action: PayloadAction<any>) => {
      state.news.sessionNews.unshift(action.payload);
      state.news.newsTitle = '';
      state.news.newsDescription = '';
    },

    deleteNewsItem: (state, action: PayloadAction<string>) => {
      state.news.sessionNews = state.news.sessionNews.filter(
        news => news.id !== action.payload
      );
    },

    setSessionNews: (state, action: PayloadAction<any[]>) => {
      state.news.sessionNews = action.payload;
    },

    // ========================================================================
    // REVIEW PROGRESS TAB ACTIONS
    // ========================================================================
    
    setCurrentNewsPage: (state, action: PayloadAction<number>) => {
      state.reviewProgress.currentNewsPage = action.payload;
    },

    setSelectedKpi: (state, action: PayloadAction<any[]>) => {
      state.reviewProgress.selectedKpi = action.payload;
    },

    // ========================================================================
    // FAVORITES ACTIONS (Shared across tabs)
    // ========================================================================
    
    setTaskToFavoriteIdMapping: (
      state,
      action: PayloadAction<{ taskId: string; favoriteId: number }>
    ) => {
      state.favorites.taskToFavoriteIdMap[action.payload.taskId] = action.payload.favoriteId;
    },

    removeTaskToFavoriteIdMapping: (state, action: PayloadAction<string>) => {
      delete state.favorites.taskToFavoriteIdMap[action.payload];
    },

    clearFavorites: (state) => {
      state.favorites.data = [];
      state.favorites.error = null;
      state.favorites.lastFetchedParams = null;
      state.favorites.taskToFavoriteIdMap = {};
    },

    // ========================================================================
    // RESET ACTIONS
    // ========================================================================
    
    resetStandupRedirect: (state) => {
      return initialState;
    },
  },

  extraReducers: (builder) => {
    builder
      // Fetch lighthouse execution (Review Performance Tab)
      .addCase(fetchLighthouseExecution.pending, (state) => {
        state.reviewPerformance.lighthouseExecution.loading = true;
        state.reviewPerformance.lighthouseExecution.error = null;
      })
      .addCase(fetchLighthouseExecution.fulfilled, (state, action) => {
        state.reviewPerformance.lighthouseExecution.loading = false;
        // New API structure: response has { success, data: { location_name, modules }, metadata }
        const responseData = action.payload.data as any;
        console.log('Full response data:', responseData);
        console.log('Modules data:', responseData?.data?.modules);
        state.reviewPerformance.lighthouseExecution.data = responseData?.data || null;
        state.reviewPerformance.lighthouseExecution.lastFetchedLocation = action.payload.locationName;
        state.reviewPerformance.lighthouseExecution.error = null;
      })
      .addCase(fetchLighthouseExecution.rejected, (state, action) => {
        state.reviewPerformance.lighthouseExecution.loading = false;
        state.reviewPerformance.lighthouseExecution.error = action.payload as string;
      })
      // Fetch favorites (Shared across Review Performance and Key Activities tabs)
      .addCase(fetchFavorites.pending, (state) => {
        state.favorites.loading = true;
        state.favorites.error = null;
      })
      .addCase(fetchFavorites.fulfilled, (state, action) => {
        console.log('Redux: fetchFavorites.fulfilled called with payload:', action.payload);
        state.favorites.loading = false;
        state.favorites.data = action.payload.data;
        state.favorites.lastFetchedParams = {
          sessionId: action.payload.params.standupSessionId,
          locationId: action.payload.params.locationId,
          standupType: action.payload.params.standupType,
        };
        state.favorites.error = null;
        console.log('Redux: Updated favorites state:', state.favorites);
      })
      .addCase(fetchFavorites.rejected, (state, action) => {
        state.favorites.loading = false;
        state.favorites.error = action.payload as string;
      })
      // Fetch lighthouse tasks
      .addCase(fetchLighthouseTasks.pending, (state) => {
        state.keyActivities.loading = true;
        state.keyActivities.error = null;
      })
      .addCase(fetchLighthouseTasks.fulfilled, (state, action) => {
        state.keyActivities.allTasks = action.payload.data;
        state.keyActivities.loading = false;
        state.keyActivities.error = null;
      })
      .addCase(fetchLighthouseTasks.rejected, (state, action) => {
        state.keyActivities.loading = false;
        state.keyActivities.error = action.payload as string;
      })
      // Fetch KPI metrics (Review Performance tab)
      .addCase(fetchKpiMetrics.pending, (state) => {
        state.reviewPerformance.kpiMetrics.loading = true;
        state.reviewPerformance.kpiMetrics.error = null;
      })
      .addCase(fetchKpiMetrics.fulfilled, (state, action) => {
        console.log('Redux: fetchKpiMetrics.fulfilled called with payload:', action.payload);
        state.reviewPerformance.kpiMetrics.loading = false;
        state.reviewPerformance.kpiMetrics.data = action.payload.data;
        state.reviewPerformance.kpiMetrics.lastFetchedParams = {
          location: action.payload.params.location,
          standupType: action.payload.params.standupType,
          metricsType: action.payload.params.metricsType,
        };
        state.reviewPerformance.kpiMetrics.error = null;
        console.log('Redux: Updated KPI metrics state:', state.reviewPerformance.kpiMetrics);
      })
      .addCase(fetchKpiMetrics.rejected, (state, action) => {
        state.reviewPerformance.kpiMetrics.loading = false;
        state.reviewPerformance.kpiMetrics.error = action.payload as string;
      });
  },
});

// ============================================================================
// EXPORTS
// ============================================================================

export const {
  // Session
  setSessionData,
  clearSessionData,
  
  // Common
  setActiveTab,
  toggleTopOpportunities,
  toggleFavoriteOpportunity,
  
  // Review Performance
  clearLighthouseExecution,
  toggleFavoriteSparcCard,
  toggleFavoriteLighthouseCard,
  setSelectedPeriod,
  
  // Key Activities
  toggleFavoriteLighthouseTask,
  setAllLighthouseTasks,
  
  // News
  setNewsTitle,
  setNewsDescription,
  addNewsItem,
  deleteNewsItem,
  setSessionNews,
  
  // Review Progress
  setCurrentNewsPage,
  setSelectedKpi,
  
  // Favorites (Shared)
  setTaskToFavoriteIdMapping,
  removeTaskToFavoriteIdMapping,
  clearFavorites,
  
  // Reset
  resetStandupRedirect,
} = standupRedirectSlice.actions;

export default standupRedirectSlice.reducer;
